import { CategoryQuestions } from '../types';

export const addictionRecoveryQuestions: CategoryQuestions = {
  title: 'Personalize Your Recovery Session',
  questions: {
    preferredStyle: {
      label: 'What approach feels most supportive for your recovery today?',
      type: 'radio',
      options: ['Gentle and compassionate', 'Direct and empowering', 'Supportive and understanding', 'Calm and grounding']
    },
    primaryGoal: {
      label: 'What would be most helpful to focus on today?',
      type: 'radio',
      options: ['Managing urges and cravings', 'Processing difficult emotions', 'Building self-compassion', 'Understanding underlying needs', 'Strengthening healthy connections', 'Healing shame and self-worth']
    },
    recoveryStage: {
      label: 'Where are you in your recovery journey?',
      type: 'radio',
      options: ['Early recovery (0-90 days)', 'Established recovery (3-12 months)', 'Long-term recovery (1+ years)', 'Exploring recovery options', 'Supporting recovery after setback']
    },
    emotionalNeeds: {
      label: 'What underlying emotional needs are you working to meet in healthier ways?',
      type: 'radio',
      options: ['Connection and intimacy', 'Stress relief and relaxation', 'Emotional regulation', 'Self-soothing and comfort', 'Excitement and stimulation', 'Validation and acceptance']
    },
    currentChallenges: {
      label: 'What recovery challenges are you facing right now?',
      type: 'textarea',
      placeholder: 'Share what feels most difficult or triggering lately (optional - helps personalize your session)'
    },
    copingStrategies: {
      label: 'What healthy coping strategies have helped you before?',
      type: 'radio',
      options: ['Physical exercise or movement', 'Mindfulness and breathing', 'Creative expression', 'Social connection', 'Spiritual practices', 'Professional support']
    },
    strengthsAwareness: {
      label: 'What personal strengths support your recovery?',
      type: 'radio',  
      options: ['Determination and persistence', 'Emotional awareness and insight', 'Courage to seek help', 'Compassion for others', 'Problem-solving abilities', 'Spiritual connection']
    },
    relationshipFocus: {
      label: 'How would you like to work on authentic connections?',
      type: 'radio',
      options: ['Building emotional intimacy', 'Improving communication skills', 'Healing relationship wounds', 'Setting healthy boundaries', 'Developing trust', 'Learning vulnerability']
    },
    additionalNotes: {
      label: 'Is there anything else the AI should know to create the perfect session for you?',
      type: 'textarea',
      placeholder: 'Any additional context, special requests, or details that would make this session more meaningful...'
    },
  },
  groupTitle: 'Personalize Your Group Recovery Session',
  groupQuestions: {
    preferredStyle: {
      label: 'What approach feels most supportive for your group\'s recovery?',
      type: 'radio',
      options: ['Gentle and compassionate', 'Direct and empowering', 'Supportive and understanding', 'Calm and grounding']
    },
    primaryGoal: {
      label: 'What would be most helpful for the group to focus on together?',
      type: 'radio',
      options: ['Shared emotional support', 'Understanding addiction together', 'Building healthy connections', 'Family healing and communication', 'Relapse prevention strategies', 'Celebrating recovery progress']
    },
    groupType: {
      label: 'What type of group recovery session are you creating?',
      type: 'radio',
      options: ['Men\'s recovery support group', 'Mixed recovery group', 'Couples recovery session', 'Family healing session', 'Recovery retreat group']
    },
    groupRecoveryStage: {
      label: 'What recovery stages are represented in your group?',
      type: 'radio',
      options: ['All early recovery (0-6 months)', 'Mixed stages of recovery', 'Established recovery group', 'Family/partners supporting recovery', 'Professional treatment setting']
    },
    groupChallenges: {
      label: 'What recovery challenges is your group working through together?',
      type: 'textarea',
      placeholder: 'Describe shared struggles, relationship issues, or healing needs...'
    },
    groupStrengths: {
      label: 'What are the group\'s collective strengths in recovery?',
      type: 'radio',
      options: ['Mutual support and understanding', 'Shared commitment to healing', 'Open communication', 'Professional guidance available', 'Family love and dedication', 'Spiritual foundation']
    },
    healingGoals: {
      label: 'What healing goals does the group share?',
      type: 'radio',
      options: ['Rebuilding trust and intimacy', 'Healing family relationships', 'Creating emotional safety', 'Supporting individual recovery', 'Building new traditions', 'Strengthening communication']
    },
    additionalNotes: {
      label: 'Is there anything else the AI should know to create the perfect session for your group?',
      type: 'textarea',
      placeholder: 'Any additional context, special requests, or details that would make this session more meaningful...'
    },
  },
  practitionerGroupTitle: 'Personalize Your Clients\' Recovery Session',
  practitionerGroupQuestions: {
    preferredStyle: {
      label: 'What approach feels most supportive for your clients\' recovery today?',
      type: 'radio',
      options: ['Gentle and compassionate', 'Direct and empowering', 'Supportive and understanding', 'Calm and grounding']
    },
    primaryGoal: {
      label: 'What would be most helpful for your clients to focus on together?',
      type: 'radio',
      options: ['Shared emotional support', 'Understanding addiction together', 'Building healthy connections', 'Family healing and communication', 'Relapse prevention strategies', 'Celebrating recovery progress']
    },
    groupType: {
      label: 'What type of group recovery session are you creating?',
      type: 'radio',
      options: ['Men\'s recovery support group', 'Mixed recovery group', 'Couples recovery session', 'Family healing session', 'Recovery retreat group']
    },
    groupRecoveryStage: {
      label: 'What recovery stages are represented in your clients?',
      type: 'radio',
      options: ['All early recovery (0-6 months)', 'Mixed stages of recovery', 'Established recovery group', 'Family/partners supporting recovery', 'Professional treatment setting']
    },
    groupChallenges: {
      label: 'What recovery challenges are your clients working through together?',
      type: 'textarea',
      placeholder: 'Describe shared struggles, relationship issues, or healing needs...'
    },
    groupStrengths: {
      label: 'What are your clients\' collective strengths in recovery?',
      type: 'radio',
      options: ['Mutual support and understanding', 'Shared commitment to healing', 'Open communication', 'Professional guidance available', 'Family love and dedication', 'Spiritual foundation']
    },
    healingGoals: {
      label: 'What healing goals do your clients share?',
      type: 'radio',
      options: ['Rebuilding trust and intimacy', 'Healing family relationships', 'Creating emotional safety', 'Supporting individual recovery', 'Building new traditions', 'Strengthening communication']
    },
    additionalNotes: {
      label: 'Is there anything else the AI should know to create the perfect session for your clients?',
      type: 'textarea',
      placeholder: 'Any additional context, special requests, or details that would make this session more meaningful...'
    },
  },
  practitionerTitle: 'Personalize Your Client\'s Recovery Session',
  practitionerQuestions: {
    preferredStyle: {
      label: 'What approach feels most supportive for your client\'s recovery today?',
      type: 'radio',
      options: ['Gentle and compassionate', 'Direct and empowering', 'Supportive and understanding', 'Calm and grounding']
    },
    primaryGoal: {
      label: 'What would be most helpful for your client to focus on today?',
      type: 'radio',
      options: ['Managing urges and cravings', 'Processing difficult emotions', 'Building self-compassion', 'Understanding underlying needs', 'Strengthening healthy connections', 'Healing shame and self-worth']
    },
    recoveryStage: {
      label: 'Where is your client in their recovery journey?',
      type: 'radio',
      options: ['Early recovery (0-90 days)', 'Established recovery (3-12 months)', 'Long-term recovery (1+ years)', 'Exploring recovery options', 'Supporting recovery after setback']
    },
    emotionalNeeds: {
      label: 'What underlying emotional needs is your client working to meet in healthier ways?',
      type: 'radio',
      options: ['Connection and intimacy', 'Stress relief and relaxation', 'Emotional regulation', 'Self-soothing and comfort', 'Excitement and stimulation', 'Validation and acceptance']
    },
    currentChallenges: {
      label: 'What recovery challenges is your client facing right now?',
      type: 'textarea',
      placeholder: 'Share what recovery challenges your client is experiencing (optional - helps personalize their session)'
    },
    copingStrategies: {
      label: 'What healthy coping strategies have helped your client before?',
      type: 'radio',
      options: ['Physical exercise or movement', 'Mindfulness and breathing', 'Creative expression', 'Social connection', 'Spiritual practices', 'Professional support']
    },
    strengthsAwareness: {
      label: 'What personal strengths support your client\'s recovery?',
      type: 'radio',  
      options: ['Determination and persistence', 'Emotional awareness and insight', 'Courage to seek help', 'Compassion for others', 'Problem-solving abilities', 'Spiritual connection']
    },
    relationshipFocus: {
      label: 'How would your client like to work on authentic connections?',
      type: 'radio',
      options: ['Building emotional intimacy', 'Improving communication skills', 'Healing relationship wounds', 'Setting healthy boundaries', 'Developing trust', 'Learning vulnerability']
    },
    additionalNotes: {
      label: 'Is there anything else the AI should know to create the perfect session for your client?',
      type: 'textarea',
      placeholder: 'Any additional context, special requests, or details that would make this session more meaningful...'
    }
  },
  giftTitle: 'Personalize Recovery Session for Your Loved One',
  giftQuestions: {
    recoveryStage: {
      label: 'Where are they in their recovery journey?',
      type: 'radio',
      options: ['Early recovery (0-90 days)', 'Established recovery (3-12 months)', 'Long-term recovery (1+ years)', 'Exploring recovery options', 'Supporting recovery after setback']
    },
    primaryFocus: {
      label: 'What would be most helpful for them to focus on?',
      type: 'radio',
      options: ['Managing urges and cravings', 'Processing difficult emotions', 'Building self-compassion', 'Understanding underlying needs', 'Strengthening healthy connections', 'Healing shame and self-worth']
    },
    currentChallenges: {
      label: 'What recovery challenges are they facing right now?',
      type: 'textarea',
      placeholder: 'Share what recovery challenges they are experiencing (optional)'
    },
    additionalNotes: {
      label: 'Anything else the AI should know to create a perfect session for them?',
      type: 'textarea',
      placeholder: 'Any additional context, special requests, or details that would make this session more meaningful...'
    },
  }
};